import React, { useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TimerIcon from '@mui/icons-material/Timer';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LensBlurIcon from '@mui/icons-material/LensBlur';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import ErrorIcon from '@mui/icons-material/Error';

import IdentifiedOrganismsBox from './IdentifiedSpecies/IdentifiedOrganismsBox';
import DetailedRunMetadataSummary from './Runs/DetailedRunMetadataSummary';
import DetailedSampleMetadataSummary from './Samples/DetailedSampleMetadataSummary';
import DetailedSequencingYieldSummary from './Samples/DetailedSequencingYieldSummary';
import DetailedSequencingQualitySummary from './Samples/DetailedSequencingQualitySummary';
import DetailedAssemblyQualitySummary from './Samples/DetailedAssemblyQualitySummary';
import BackButton from './Buttons/BackButton';
import IdentifiedSpeciesFiltersDialog from './Samples/IdentifiedSpeciesFiltersDialog';
import { IdentifiedSpeciesFiltersDispatchContext } from './context/IdentifiedSpeciesFiltersContext';
import { sortByIdConfidence } from './IdentifiedSpecies/utilities';
import { useDataset, useSample, useActiveAnalysis, useIdentifiedSpecies, useAmrPredictions, useResistanceGenes, useImputations } from './services/api/KeynomeAPIHooks';
import ClonalSamplesList from './Samples/ClonalSamplesList.jsx'

function Sample() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // If the user navigates from one sample page to another (e.g. through a clonality link) we reset 
  // the scroll to the top of the page to make it clear that the sample being viewed is different.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const { 
    activeProject, 
    organization, 
    hasAccessReportViruses, 
    setUserErrorMessage 
  } = useOutletContext();

  const { setVisibleSpeciesCt, setLoadingResistanceGenes, setLoadingImputations } = useContext(IdentifiedSpeciesFiltersDispatchContext);

  const { data: run, isLoading: runLoading } = useDataset(
    params.runId, { extraFields: "keynome_web_url" }
  );
  const { data: sample, isLoading: sampleLoading } = useSample(params.sampleId);
  const { data: activeAnalysis, isLoading: analysisLoading } = useActiveAnalysis(
    params.sampleId,
    {
      useQueryOptions: { refetchInterval: 20_000 }, // Poll the analysis status every 20 seconds
      queryParams: { extraFields: ["estimated_minutes_to_result", "status"] }
    }
  );

  // If the sample fails to load, then return to the main page and provide an error
  useEffect(() => {
    if (!sampleLoading && !sample) {
      setUserErrorMessage(`The requested sample ${params.sampleId} does not exist in your project! Contact support@dayzerodiagnostics if you require assistance.`)
      console.error(`Sample with UUID ${params.sampleId} not found`)
      navigate("/", { replace: true, state: location.state })
    }
  }, [sample, params, setUserErrorMessage, location.state, navigate, sampleLoading])

  const { data: identifiedSpecies, isLoading: identifiedSpeciesLoading } = useIdentifiedSpecies(activeAnalysis?.id);

  const accessibleIdentifiedSpecies = useMemo(() => sortByIdConfidence(
    identifiedSpecies?.filter(item => hasAccessReportViruses || !item.attributes.is_virus)
  ), [identifiedSpecies, hasAccessReportViruses]);
  const positiveIdentifiedSpecies = accessibleIdentifiedSpecies
    ?.filter(item => item.attributes.species_call_positive)

  // Fetch resistance genes and imputations for sample to manage loading state
  const { data: amrPredictions, isLoading: predicationLoading } = useAmrPredictions(positiveIdentifiedSpecies.map(species => species.id));
  const { isLoading: resistanceGenesLoading } = useResistanceGenes(positiveIdentifiedSpecies.map(species => species.id));
  const { isLoading: imputationsLoading } = useImputations(amrPredictions?.map(prediction => prediction?.id));

  const dataLoading = runLoading || sampleLoading || analysisLoading || identifiedSpeciesLoading || predicationLoading;
  useEffect(() => {
    // Set loading states for resistance genes and imputations (disable/enable toggles in IdentifiedSpeciesFiltersDialog)
    setLoadingResistanceGenes(dataLoading || resistanceGenesLoading);
    setLoadingImputations(dataLoading || imputationsLoading);
  }, [dataLoading, resistanceGenesLoading, imputationsLoading, setLoadingResistanceGenes, setLoadingImputations]);

  const isIllumina = run?.attributes?.sequencing_platform === 'illumina' || run?.attributes?.platform === 'illumina';

  const microbialMegabases = accessibleIdentifiedSpecies
    .reduce((sum, species) => sum + (species.attributes.reads_megabases_to_species ?? 0), 0);
  const microbialReads = accessibleIdentifiedSpecies
    .reduce((sum, species) => sum + (species.attributes.reads_to_species ?? 0), 0);

  useEffect(() => {
    if (isIllumina) {
      const initialVisibleIdentifiedOrganismsCt = accessibleIdentifiedSpecies
        .filter(item => ((item.attributes.reads_megabases_to_species ?? 0) / microbialMegabases) >= 0.01)
        .length
      setVisibleSpeciesCt(Math.min(initialVisibleIdentifiedOrganismsCt, 5));
    }
    else {
      const kidPositiveSpeciesCt = accessibleIdentifiedSpecies
        .filter(item => item.attributes.species_call_positive)
        .length;
        setVisibleSpeciesCt(Math.min(accessibleIdentifiedSpecies.length, Math.max(kidPositiveSpeciesCt, 5)));
    }
  }, [isIllumina, accessibleIdentifiedSpecies, microbialMegabases, setVisibleSpeciesCt]);

  // Get the URL for the sample's dataset, to use for the back button
  const datasetUrl = run ? new URL(run.attributes.keynome_web_url) : null;

  return (
    <Box>
      <WaitingDialog
        analysis={activeAnalysis}
        backToLink={datasetUrl?.pathname}
        dataLoading={sampleLoading || analysisLoading}
      />
      <Grid container sx={{ borderBottom: '#bdbdbd 2px solid' }}>
        <Grid item xs={12}>
          <Box>
            <Grid container maxWidth="lg" sx={{ m: 'auto', p: '10px' }}>
              <Grid item xs={2} sx={{ borderRight: '#bdbdbd 2px solid' }}>
                <Box>
                  <BackButton backToLink={datasetUrl?.pathname} backButtonText="ALL SAMPLES" />
                </Box>
              </Grid>
              <Grid item xs={4} align="left" sx={{ pt: 0.5, pl: 2 }}>
                Keynome Cloud RUO Results
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Grid container maxWidth="lg" sx={{ m: 'auto', mb: 2, paddingRight: '30px' }} spacing={2} align="auto">
          <Grid item xs={12}>
              <Alert severity="warning"><b>For Research Use Only.</b> Not for use in diagnostic procedures.</Alert>
          </Grid>

        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedRunMetadataSummary run={run} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedSampleMetadataSummary sample={sample} analysis={activeAnalysis} activeProject={activeProject} isIllumina={isIllumina} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Box>
            <IdentifiedOrganismsBox
              isDiagnosticView={false}
              identifiedSpecies={accessibleIdentifiedSpecies}
              isIllumina={isIllumina}
              run={run}
              sample={sample}
              analysis={activeAnalysis}
              totalMicrobialMegabases={microbialMegabases}
              organization={organization}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={2}>
            <ClonalSamplesList sample={sample} />
          </Paper>
        </Grid>
        <IdentifiedSpeciesFiltersDialog 
            isIllumina={isIllumina}
            identifiedSpecies={accessibleIdentifiedSpecies}
        />
            <>
        <Grid item xs={12}>
          <Paper elevation={2}>
            <DetailedSequencingYieldSummary
              analysis={activeAnalysis}
              microbialMegabases={microbialMegabases}
              microbialReads={microbialReads}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedSequencingQualitySummary analysis={activeAnalysis} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedAssemblyQualitySummary analysis={activeAnalysis} 
            isIllumina={isIllumina}
            />
          </Paper>
        </Grid>
            </>
      </Grid>
    </Box>
  );
}

export default Sample;

/**
  * A small dialog that sits on top of the sample page if the same is not ready for review
  */
function WaitingDialog(props) {
  const { backToLink, analysis, dataLoading } = props;

  // Extract the status and time estimate from the analysis
  const status = analysis?.attributes.status;
  const estimated_minutes_to_result = parseInt(
    analysis?.attributes.estimated_minutes_to_result
  );

  // Construct an additional message for the estimate to put in the dialog
  let estimate_message;
  if (status === "analyzing") {
    if (estimated_minutes_to_result <= 0) {
      estimate_message = "Results are almost ready.";
    } else if (estimated_minutes_to_result > 0) {
      estimate_message = `Estimated time remaining: ~ ${estimated_minutes_to_result} min`;
    } else {
      // The estimate can be null if the analysis just started and we don't yet have the yield 
      // numbers computed to perform the estimate 
      estimate_message = "Estimated time remaining: - min";
    }
  }

  return (
    <Dialog open={!dataLoading && status !== "succeeded"}>
      <DialogTitle>
        <BackButton backToLink={backToLink} backButtonText="BACK TO DATASET" />
      </DialogTitle>
      <DialogContent>
        <StatusIndicator status={status}/>
        <DialogContentText>
          <p>
            {status !== "failed" && 
              "We're still working on your analysis, please check back later."}
            <br />
            Contact <a href="mailto:support@dayzerodiagnostics.com">support@dayzerodiagnostics.com</a> if you require assistance.
          </p>
          {estimate_message && 
            <span>
              <TimerIcon height="20px" sx={{ marginBottom: "-7px" }}/>
              {'  '}
              {estimate_message}
            </span>
          }
          
        </DialogContentText>
      </DialogContent>
    </Dialog> 
  )
}

function StatusIndicator(props) {
  const { status } = props;

  let statusIcon; 
  let statusIconColor;
  if (status === 'analyzing') {
    statusIcon = <LensBlurIcon height="20px" sx={{marginBottom: "-5px"}} />
    statusIconColor = '#ed6c02'
  } else if (status === 'delayed') {
    statusIcon = <WatchLaterIcon height="20px" sx={{marginBottom: "-5px"}} />
    statusIconColor = '#ed6c02'
  } else if (status === 'failed') {
    statusIcon = <ErrorIcon height="20px" sx={{marginBottom: "-5px"}} />
    statusIconColor = '#ed6c02'
  } else {
    statusIcon = <CloudUploadIcon height="20px" sx={{marginBottom: "-5px"}} />
    statusIconColor = 'gray'
  }

  let statusText;
  if (status === 'analyzing') {
    statusText = 'Analyzing'
  } else if (status === 'delayed') {
    statusText = 'Delayed'
  } else if (status === 'failed') {
    statusText = 'Failed'
  } else {
    statusText = 'Analysis starting'
  }

  return (
    <span style={{color: statusIconColor}}>
    {statusIcon}
    {'  '}
    {statusText}
    </span>
  )
}
